/**
 * Who reads money, and which `organizations` columns the app still selects through PostgREST
 * (app#376; docs/PERMISSIONS.md § 5 Org: "Read usage — any member", "Read money data (invoices,
 * card, credit balance, spend cap) — Owner").
 *
 * ⚠⚠ ROUTER MIGRATION 0222 (router#1766, PR router#1800) WITHHOLDS TWO COLUMNS FROM EVERY APP USER,
 * THE OWNER INCLUDED: `organizations.spend_cap_cents` and `organizations.subscription_status`. A
 * PostgREST select that NAMES a withheld column fails AS A WHOLE with 42501, so a select that kept
 * either one would empty the Billing and Usage pages (and the banner on every page) for every user
 * the moment 0222 is applied. The Owner reads the two values from the router instead:
 * `spend_cap_cents` from `credit` op=status, `subscription_status` from `billing`. 0222 also makes
 * `get_org_credit` (the credit grants) Owner only (42501 for Admin and Member).
 * This app change must ship BEFORE 0222 is applied.
 *
 * Pure, so `moneyReads.test.ts` checks it with `node --experimental-strip-types`.
 */

/** The two `organizations` columns 0222 withholds. The app must never select them. */
export const WITHHELD_ORG_COLUMNS = ['spend_cap_cents', 'subscription_status'] as const

/** `useBilling.get`'s `organizations` select: usage and plan state, readable by every member. */
export const ORG_BILLING_COLUMNS = 'current_period_end, plan_id, throttled_until, blocked_until'

/** `useUsage`'s `organizations` select: the plan's name and flat fee, for the page header. */
export const ORG_USAGE_COLUMNS = 'plans(name, monthly_fee_cents)'

/**
 * Whether the reader of the active org may read money data: the org's Owner only.
 * ⚠ The same rule as `allows(role, 'billing.manage')` in `orgRole.ts`, and the router's
 * `ActionManageBilling`. A null role reads nothing.
 */
export const readsMoney = (role: string | null | undefined): boolean => role === 'owner'

/** The statuses the router's `billing` tool documents for `subscription_status`. */
const STATUSES = new Set(['active', 'past_due', 'unpaid', 'canceled', 'inactive'])

/**
 * `subscription_status` from a `billing` payload, or null when the payload carries none this app
 * knows. ⚠ Null is "not read", never a status: the caller keeps what it had.
 */
export function subscriptionStatusFrom(payload: unknown): string | null {
  const status = (payload as { subscription_status?: unknown } | null | undefined)?.subscription_status
  return typeof status === 'string' && STATUSES.has(status) ? status : null
}
